import BushfireWarningCarousel from 'components/bushfire-warning-carousel';
import FireDangerCarousel from 'components/fire-danger-carousel';
import { H4 } from 'components/headings';
import InfographicDialog from 'components/infographic-dialog';
import Layout from 'components/layout';
import SEO from 'components/seo';
import ToolsCard from 'components/tools-card';
import ContentSection from 'components/ui/content-section';
import { Text } from 'components/ui/text';
import { NextPage } from 'next';
import React, { MouseEvent, useState } from 'react';
import { useEffectOnce } from 'react-use';
import AnalyticsService, { logModalDialogOpened } from 'services/analytics';
import { backgroundImages } from 'utils/config';

interface stayInformedFormat {
  icon:
    | 'emergency'
    | 'facebook'
    | 'phone'
    | 'radio'
    | 'warning-system'
    | 'fire-danger-ratings'
    | 'conversation'
    | 'resource';
  title: string;
  description: string;
  href: string;
  handleClick?: string;
}

const PAGE_TITLE = 'Stay Informed';
const PAGE_DESCRIPTION =
  'Use these sources to keep up-to-date with official warnings, alerts and hazard information for your area.';

const stayInformedInfo: stayInformedFormat[] = [
  {
    icon: 'emergency',
    title: 'Emergency WA',
    description:
      'Official warnings and information for bushfires and other hazards.',
    href: 'https://www.emergency.wa.gov.au/',
  },
  {
    icon: 'facebook',
    title: 'DFES WA Facebook',
    description:
      'Stay up-to-date with DFES warnings, safety information and news.',
    href: 'https://www.facebook.com/dfeswa',
  },
  {
    icon: 'phone',
    title: 'DFES Emergency Information Line',
    description:
      'For recorded emergency information and alerts call 13 DFES (13 3337).',
    href: '#',
  },
  {
    icon: 'radio',
    title: 'ABC Radio',
    description:
      'Tune in to your local ABC radio station for bushfire warnings and updates.',
    href: 'https://www.abc.net.au/radio/stations/',
  },
  {
    icon: 'conversation',
    title: 'Your community',
    description:
      'Keep in touch with friends, neighbours and community networks to stay informed.',
    href: 'https://dfes.wa.gov.au/site/bushfire/prepare.html#bushfire-ready',
  },
  {
    icon: 'warning-system',
    title: 'Bushfire Warning System',
    description:
      'Understand the bushfire warning system and what to do at each warning level.',
    href: '#',
    handleClick: 'warningSystem',
  },
  {
    icon: 'fire-danger-ratings',
    title: 'Fire Danger Ratings',
    description: 'Learn more about Fire Danger Ratings and what to do.',
    href: '#',
    handleClick: 'fireDanger',
  },
  {
    icon: 'resource',
    title: 'Bushfire preparation checklists and resources',
    description:
      'View and download useful factsheets to help you prepare ahead of bushfire season.',
    href: '/emergency-resources',
  },
];

const StayInformed: NextPage = () => {
  useEffectOnce(() => {
    AnalyticsService.trackScreen('stay_informed');
  });

  const [showBushfireWarningDialog, setShowBushfireWarningDialog] =
    useState(false);
  const [showFireDangerDialog, setShowFireDangerDialog] = useState(false);

  const openBushfireWarningDialog = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setShowBushfireWarningDialog(true);
    logModalDialogOpened(undefined, 'bushfire_warning_levels');
  };

  const openFireDangerDialog = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setShowFireDangerDialog(true);
    logModalDialogOpened(undefined, 'fire_danger_ratings');
  };

  const closeBushfireWarningDialog = () => setShowBushfireWarningDialog(false);
  const closeFireDangerDialog = () => setShowFireDangerDialog(false);

  return (
    <Layout mainBackground={backgroundImages.hero}>
      <SEO title={PAGE_TITLE} description={PAGE_DESCRIPTION} />
      <ContentSection textAlign='center'>
        <H4 as='h1' style={{ paddingBottom: '20px' }}>
          Stay Informed
        </H4>
        <Text size='large'>
          Use these sources to keep up-to-date with official warnings, alerts
          and hazard information for your area.
        </Text>
        <Text size='large'>
          It’s important to stay alert and monitor your surroundings for
          changing conditions or signs of a bushfire. If you think you’re in
          danger don’t wait for a warning to be issued - put your bushfire plan
          into action for your own safety.
        </Text>
        {stayInformedInfo.map((info, index) => {
          return (
            <>
              {info.handleClick === 'warningSystem' && (
                <ToolsCard
                  icon={info.icon}
                  title={info.title}
                  description={info.description}
                  href={info.href}
                  handleClick={openBushfireWarningDialog}
                  key={index}
                />
              )}
              {info.handleClick === 'fireDanger' && (
                <ToolsCard
                  icon={info.icon}
                  title={info.title}
                  description={info.description}
                  href={info.href}
                  handleClick={openFireDangerDialog}
                  key={index}
                />
              )}
              {!info.handleClick && (
                <ToolsCard
                  icon={info.icon}
                  title={info.title}
                  description={info.description}
                  href={info.href}
                  key={index}
                />
              )}
            </>
          );
        })}
      </ContentSection>
      <InfographicDialog
        showDialog={showBushfireWarningDialog}
        closeDialog={closeBushfireWarningDialog}
      >
        <BushfireWarningCarousel />
      </InfographicDialog>
      <InfographicDialog
        showDialog={showFireDangerDialog}
        closeDialog={closeFireDangerDialog}
      >
        <FireDangerCarousel />
      </InfographicDialog>
    </Layout>
  );
};

export default StayInformed;
